import { useMemo } from "react";
import { useI18n } from "../i18n/useI18n";
import { numberLocaleFor } from "../i18n/locale";
import { AWS_REGION_IDS, type AwsRegionId } from "../lib/awsRegion";
import type { AudienceMode } from "../lib/audienceMode";
import { estimateMonthlyCost } from "../lib/costEngine";
import type { DeploymentStackConfig } from "../lib/deploymentStack";
import type { ComputeProfileId } from "../lib/computeProfile";
import type { LlmPriceProfile } from "../lib/llmPriceProfile";
import type { SavingsLevers } from "../lib/savingsLevers";
import type { UiKey } from "../i18n/ui";
import type { Workload } from "../lib/types";

type Props = {
  t: (key: UiKey) => string;
  workload: Workload;
  savings: SavingsLevers;
  stack: DeploymentStackConfig;
  llmProfile: LlmPriceProfile;
  computeProfile: ComputeProfileId;
  selectedRegion: AwsRegionId;
  audienceMode: AudienceMode;
};

export function RegionCompareTable({
  t,
  workload,
  savings,
  stack,
  llmProfile,
  computeProfile,
  selectedRegion,
  audienceMode,
}: Props) {
  const { locale } = useI18n();
  const nf = numberLocaleFor(locale);
  const fmt = (n: number) =>
    n.toLocaleString(nf, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  const baselineTotal = useMemo(
    () =>
      estimateMonthlyCost(
        workload,
        locale,
        selectedRegion,
        savings,
        stack,
        llmProfile,
        audienceMode,
        computeProfile,
      ).totalMonthlyUsd,
    [
      workload,
      locale,
      selectedRegion,
      savings,
      stack,
      llmProfile,
      audienceMode,
      computeProfile,
    ],
  );

  function formatDelta(id: AwsRegionId, totalUsd: number): string {
    if (id === selectedRegion) return "—";
    if (baselineTotal <= 0) return "—";
    const pct = (totalUsd / baselineTotal - 1) * 100;
    const sign = pct > 0 ? "+" : "";
    return `${sign}${pct.toFixed(1)}%`;
  }

  return (
    <div className="region-compare">
      <h2>{t("regionCompare.title")}</h2>
      <p className="hint">{t("regionCompare.hint")}</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">{t("regionCompare.colRegion")}</th>
              <th scope="col">{t("regionCompare.colTotal")}</th>
              <th scope="col" className="num">
                {t("regionCompare.colDelta")}
              </th>
            </tr>
          </thead>
          <tbody>
            {AWS_REGION_IDS.map((id) => {
              const totalUsd = estimateMonthlyCost(
                workload,
                locale,
                id,
                savings,
                stack,
                llmProfile,
                audienceMode,
                computeProfile,
              ).totalMonthlyUsd;
              const isSel = id === selectedRegion;
              return (
                <tr key={id} className={isSel ? "selected" : undefined}>
                  <td>{t(`region.${id}` as UiKey)}</td>
                  <td className="num">{fmt(totalUsd)}</td>
                  <td className="num delta">
                    {formatDelta(id, totalUsd)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="delta-foot">{t("regionCompare.deltaFoot")}</p>
      <style>{`
        .region-compare {
          margin-top: 0.5rem;
        }
        .region-compare h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .hint {
          margin: 0 0 0.75rem;
          font-size: 0.82rem;
          color: var(--muted);
          line-height: 1.45;
        }
        .table-wrap {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          margin: 0 -0.35rem;
          padding: 0 0.35rem 0.25rem;
        }
        table {
          width: 100%;
          min-width: 20rem;
          border-collapse: collapse;
          font-size: 0.88rem;
        }
        th,
        td {
          text-align: left;
          padding: 0.55rem 0.7rem;
          border-bottom: 1px solid var(--border);
        }
        @media (max-width: 640px) {
          th,
          td {
            padding: 0.65rem 0.75rem;
            font-size: 0.92rem;
          }
        }
        th {
          color: var(--muted);
          font-weight: 600;
        }
        .num {
          font-family: var(--mono);
          text-align: right;
          color: var(--accent);
        }
        tr.selected td {
          background: var(--accent-chip);
        }
        .delta {
          color: var(--muted);
          font-size: 0.82rem;
        }
        tr.selected .delta {
          color: var(--accent);
        }
        .delta-foot {
          margin: 0.5rem 0 0;
          font-size: 0.72rem;
          color: var(--muted);
          line-height: 1.4;
        }
      `}</style>
    </div>
  );
}
